// Lógica pura de filtro/busca/ordenação da lista de Projetos (Etapa 10,
// spec S02). Separada da tela (src/ui/projects/ProjectsListScreen.tsx) para
// ser testável sem banco/React — a tela só chama esta função sobre o array
// já carregado do SQLite local.
//
// Não busca no banco nem pagina: para o volume deste app (catálogo pessoal
// de uma única usuária — a spec fala em "50+ projetos" como teto de
// referência de desempenho), um filtro/sort em memória sobre o array
// completo já carregado é instantâneo e mais simples que construir a
// mesma lógica em SQL duas vezes (aqui e numa query).
import type { Project } from '../db/schema';

export type ProjectStatusFilter = 'all' | Project['status'];

export interface ProjectListFilters {
  /** `'all'` (padrão) esconde projetos cancelados — ver regra da spec:
   * "projeto cancelado ... some do filtro default". Passar 'cancelled'
   * explicitamente mostra só os cancelados. */
  status?: ProjectStatusFilter;
  /** Busca por nome, case-insensitive, substring. Vazio/undefined = sem filtro. */
  query?: string;
}

export type SortableProject = Pick<Project, 'name' | 'status' | 'lastStitchAt' | 'createdAt'>;

/**
 * Chave de ordenação: `last_stitch_at desc` é a regra da spec ("o que
 * trabalhei por último em cima"), mas um projeto recém-criado ainda não
 * tem `last_stitch_at` (só é preenchido a partir da Etapa 12, cronômetro).
 * Sem o fallback para `created_at`, todo projeto novo teria `NULL` e cairia
 * para o fim da lista — quebrando o critério de aceite "criar projeto só
 * com nome ... e vê-lo no topo da lista". `created_at` de um projeto
 * recém-criado é "agora", então o fallback resolve os dois casos com a
 * mesma regra: mais recente (trabalhado ou criado) primeiro.
 */
function sortKey(project: SortableProject): number {
  return (project.lastStitchAt ?? project.createdAt).getTime();
}

export function filterAndSortProjects<T extends SortableProject>(
  projects: T[],
  filters: ProjectListFilters = {}
): T[] {
  const status = filters.status ?? 'all';
  const query = (filters.query ?? '').trim().toLowerCase();

  return projects
    .filter((project) => {
      if (status === 'all') {
        if (project.status === 'cancelled') return false;
      } else if (project.status !== status) {
        return false;
      }

      if (query && !project.name.toLowerCase().includes(query)) return false;

      return true;
    })
    .sort((a, b) => sortKey(b) - sortKey(a));
}
